import { openDB, type IDBPDatabase } from 'idb';

// Side-channel IDB store for attachment blobs.  Replicache stores the
// metadata; this store holds the actual bytes until they've been uploaded
// to MinIO.  Keyed by attachment id (same as the Replicache row id), so
// purging by id after upload is trivial.
//
// Kept in its own database (separate from Replicache's internal stores)
// because (a) we don't want to fight Replicache's schema management, and
// (b) blobs can be large and we don't want them riding along on every
// Replicache read.

const DB_NAME = 'seneca-attachments';
const DB_VERSION = 1;
const BLOB_STORE = 'blobs';

type BlobRecord = {
    id: string;
    blob: Blob;
    filename: string;
    mimeType: string;
    createdAt: number;
};

let dbPromise: Promise<IDBPDatabase> | null = null;

function getDb(): Promise<IDBPDatabase> {
    if (!dbPromise) {
        dbPromise = openDB(DB_NAME, DB_VERSION, {
            upgrade(db) {
                if (!db.objectStoreNames.contains(BLOB_STORE)) {
                    db.createObjectStore(BLOB_STORE, { keyPath: 'id' });
                }
            },
        });
    }
    return dbPromise;
}

export async function stageBlob(
    id: string,
    blob: Blob,
    filename: string,
    mimeType: string,
): Promise<void> {
    const db = await getDb();
    const record: BlobRecord = {
        id,
        blob,
        filename,
        mimeType,
        createdAt: Date.now(),
    };
    await db.put(BLOB_STORE, record);
}

export async function getStagedBlob(id: string): Promise<Blob | null> {
    const db = await getDb();
    const record = (await db.get(BLOB_STORE, id)) as BlobRecord | undefined;
    return record?.blob ?? null;
}

export async function purgeBlob(id: string): Promise<void> {
    const db = await getDb();
    await db.delete(BLOB_STORE, id);
}

export async function stagedIds(): Promise<string[]> {
    const db = await getDb();
    return (await db.getAllKeys(BLOB_STORE)) as string[];
}
